import type { AssistantStartInput } from "../model/assistantStore";
import { ContentAccessErrorTypeType } from "@/types/graphql/site.generated";

/** Коды ошибок, для которых экраны плеера/оплаты показывают кнопку помощника
 * (Task 15). Коды доступа приходят из ContentAccessErrorTypeType (Content.
 * siteAccess/embedAccess). PLAYBACK_ERROR и PAYMENT_FAILED: это уже коды
 * самого фронтенда, серверного типа под них нет. */
export type FixWithAssistantErrorCode =
  | ContentAccessErrorTypeType
  | "PLAYBACK_ERROR"
  | "PAYMENT_FAILED";

// Явная карта код → ключ перевода (не шаблонная строка): опечатка в ключе
// ловится typecheck'ом, а не молча рендерит пустой текст.
export const ASSISTANT_ERROR_PROMPT_KEYS: Record<
  FixWithAssistantErrorCode,
  string
> = {
  [ContentAccessErrorTypeType.ContentIsNotAvailableInCountryType]:
    "assistant.errorPrompt.CONTENT_IS_NOT_AVAILABLE_IN_COUNTRY",
  [ContentAccessErrorTypeType.ContentIsNotAvailableForFreeViewingType]:
    "assistant.errorPrompt.CONTENT_IS_NOT_AVAILABLE_FOR_FREE_VIEWING",
  [ContentAccessErrorTypeType.ContentIsNotAvailableByAccessStartAtType]:
    "assistant.errorPrompt.CONTENT_IS_NOT_AVAILABLE_BY_ACCESS_START_AT",
  [ContentAccessErrorTypeType.ContentIsNotAvailableForGuestsType]:
    "assistant.errorPrompt.CONTENT_IS_NOT_AVAILABLE_FOR_GUESTS",
  [ContentAccessErrorTypeType.ContentIsNotAvailableByParallelLimitType]:
    "assistant.errorPrompt.CONTENT_IS_NOT_AVAILABLE_BY_PARALLEL_LIMIT",
  PLAYBACK_ERROR: "assistant.errorPrompt.PLAYBACK_ERROR",
  PAYMENT_FAILED: "assistant.errorPrompt.PAYMENT_FAILED",
};

/** Предел строки ошибки плеера; бэкенд режет так же (postMessage.ts). */
export const PLAYER_ERROR_MAX_LENGTH = 300;

/** Контекст ошибки, который кнопка передаёт в первый ход. */
export interface AssistantErrorContext {
  contentId?: string | null;
  /** Продукт, который зритель покупал (диалог оплаты открыт не только на
   * /product/<id>). Без него берётся продукт из адреса страницы. */
  productId?: string | null;
  paymentId?: string | null;
  /** Тип и детали ошибки плеера, HTTP-код (PLAYBACK_ERROR). */
  playerError?: string | null;
}

/** Собирает аргумент openAssistant для кнопки «Разобраться с помощником»:
 * вынесено из FixWithAssistantButton, чтобы клик по кнопке можно было
 * проверить без DOM (в проекте нет jsdom, renderToString не кликает). */
export function buildAssistantErrorStart(
  t: (key: string) => string,
  errorCode: FixWithAssistantErrorCode,
  context: AssistantErrorContext = {},
): AssistantStartInput {
  const playerError = context.playerError?.trim();
  return {
    origin: "ERROR",
    errorCode,
    contentId: context.contentId,
    productId: context.productId,
    paymentId: context.paymentId,
    playerError: playerError
      ? playerError.slice(0, PLAYER_ERROR_MAX_LENGTH)
      : null,
    prompt: t(ASSISTANT_ERROR_PROMPT_KEYS[errorCode]),
  };
}
